import { useCallback } from 'react';

import { enqueueSnackbar } from 'notistack';

import callAzureFunction from 'utils-ts/call-azure-function';
import callAzureFunctionPublic from 'utils-ts/call-azure-function-public';
import handleError from 'utils-ts/handle-error';
import { useSelector } from 'react-redux';
import { appConfigPaths, type PathNode, type BankDetails, type CreditNote, type CreditNotePdfData } from '@a1/shared';
import { type AppConfigRoot } from '@a1/shared';
import { saveAs } from 'file-saver';

const useCreditNotePdf = () => {
  const pdfHeaderImgUrl = useSelector((state: AppConfigRoot) => state?.appConfig?.data?.images?.pdfHeader);

  const getBankDetails = useCallback(async () => {
    let bankDetails: BankDetails | null = null;

    try {
      const paymentsConfigPaths = appConfigPaths.payments as PathNode;
      const keys = Object.values(paymentsConfigPaths).map((node) => (node as PathNode).path);

      const { data } = await callAzureFunctionPublic<{ configs: { payments: BankDetails } }>({
        url: 'public/app-configurations',
        method: 'get',
        params: {
          keys,
          includeLocalStorageFalse: true
        }
      });
      bankDetails = data.configs.payments;
    } catch (error) {
      const message = handleError(error);
      enqueueSnackbar({
        variant: 'error',
        message,
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
    }

    return bankDetails;
  }, []);

  const generateCreditNotePdf = useCallback(
    async ({ creditNoteId, creditNoteData, filename }: { creditNoteId?: string; creditNoteData?: CreditNote; filename?: string }) => {
      const bankDetails = await getBankDetails();

      const data = {
        filename: `CN-${creditNoteId}.pdf`,
        bankDetails,
        pdfHeaderImgUrl,
        creditNoteId, // this will be ignored in the backend if `creditNoteData` is provided
        creditNoteData
      };

      try {
        const res = await callAzureFunction<Blob>({
          url: 'credit-note-pdf-generation',
          method: 'POST',
          responseType: 'blob',
          data
        });

        const finalName = filename || `CN-${creditNoteId || 'credit-note'}.pdf`;
        saveAs(new Blob([res.data], { type: 'application/pdf' }), finalName);
      } catch (error) {
        const message = handleError(error);
        enqueueSnackbar({
          variant: 'error',
          message,
          anchorOrigin: { vertical: 'top', horizontal: 'right' }
        });
      }
    },
    [getBankDetails, pdfHeaderImgUrl]
  );

  const getCreditNotePdfData = useCallback(
    async ({ creditNoteId, creditNoteData }: { creditNoteId?: string; creditNoteData?: CreditNote }): Promise<CreditNotePdfData | null> => {
      const bankDetails = await getBankDetails();

      const data = {
        bankDetails,
        pdfHeaderImgUrl,
        creditNoteId, // this will be ignored in the backend if `creditNoteData` is provided
        creditNoteData
      };

      try {
        const res = await callAzureFunction<Blob>({
          url: 'credit-note-pdf-generation',
          method: 'POST',
          responseType: 'blob',
          data
        });

        const pdf = res.data;

        const blob = new Blob([pdf], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);

        return {
          blob,
          url,
          bankDetails
        };
      } catch (error) {
        const message = handleError(error);
        enqueueSnackbar({
          variant: 'error',
          message,
          anchorOrigin: { vertical: 'top', horizontal: 'right' }
        });
        return null;
      }
    },
    [getBankDetails, pdfHeaderImgUrl]
  );

  return { getBankDetails, generateCreditNotePdf, getCreditNotePdfData };
};

export default useCreditNotePdf;
